import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNowStrict } from 'date-fns';
import {
  FiFolder, FiChevronRight, FiChevronLeft, FiSearch, FiInbox, FiImage, FiX, FiPlus, FiStar,
  FiLayers, FiAlertCircle, FiTrendingUp, FiZap, FiArrowRight, FiEdit3, FiClock, FiMessageSquare,
  FiCheckCircle, FiPlay, FiSend, FiXCircle, FiFlag, FiRotateCcw, FiLock, FiUploadCloud
} from 'react-icons/fi';
import { useTicketStore } from '../../store/ticketStore';
import { useAuthStore } from '../../store/authStore';
import { useShallowStore } from '../../hooks/useShallowStore';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { SkeletonLine, SkeletonBlock } from '../common/Skeleton.jsx';
import { STATUS_LABELS, STAGES, PRIORITIES, stageOf, personName } from './ticketStatus';
import './Tickets.css';

// Projects hub → one project's tickets (list + create panel) → ticket detail.
// Shared by staff (TicketManagement) and clients (ClientPortal).

const MAX_IMAGES = 6;
const PROJECT_CREATOR_ROLES = ['ceo', 'co_ceo', 'company_manager', 'sales_head', 'manager'];
const ARCHIVED_STATUSES = ['completed', 'cancelled'];
const FAVORITES_KEY = 'bigwals:ticket-favorite-projects';

const idOf = (x) => String(x?._id || x?.id || x || '');
const shortTime = (date) => (date ? format(new Date(date), 'd MMM, h:mm a') : '');
const shortDate = (date) => (date ? format(new Date(date), 'd MMM') : '');
const isActiveTicket = (t) => !t.pending && !['deployed', 'closed'].includes(stageOf(t.status));
const isDoneTicket = (t) => ['completed', 'deployed'].includes(stageOf(t.status));
const isArchived = (p) => ARCHIVED_STATUSES.includes(p?.status);
const cap = (s = '') => s.charAt(0).toUpperCase() + s.slice(1);
const initials = (u) => `${u?.firstName?.[0] || ''}${u?.lastName?.[0] || ''}`.toUpperCase() || (u?.username?.[0] || '?').toUpperCase();

// "Crew Check" → PRJ-CC, "CrewCheck" → PRJ-CRE
const projectKey = (name = '') => {
  const words = name.replace(/[^a-zA-Z0-9\s]/g, '').split(/\s+/).filter(Boolean);
  const key = words.length > 1 ? words.map(w => w[0]).join('').slice(0, 3) : (words[0] || 'TKT').slice(0, 3);
  return `PRJ-${key.toUpperCase()}`;
};

const STATUS_ICONS = {
  open: <FiClock />, in_progress: <FiPlay />, completed: <FiCheckCircle />, deployed: <FiSend />, closed: <FiXCircle />
};

const loadFavorites = () => {
  try { return new Set(JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]')); } catch { return new Set(); }
};
const saveFavorites = (set) => {
  try { localStorage.setItem(FAVORITES_KEY, JSON.stringify([...set])); } catch { /* storage unavailable */ }
};

const listSelector = (state) => ({
  tickets: state.tickets,
  fetchTickets: state.fetchTickets,
  loading: state.loading,
});

const sendSelector = (state) => ({ sendTicket: state.sendTicket });

function Avatar({ user, size = 26 }) {
  const style = { width: size, height: size, fontSize: Math.round(size * 0.4) };
  return user?.profilePicture
    ? <img className="tk-av" style={style} src={user.profilePicture} alt={personName(user)} title={personName(user)} />
    : <span className="tk-av" style={style} title={personName(user)}>{initials(user)}</span>;
}

function AvatarStack({ users = [], max = 3 }) {
  if (!users.length) return <span className="tk-muted-sm">No members</span>;
  return (
    <span className="tk-av-stack">
      {users.slice(0, max).map(u => <Avatar key={idOf(u)} user={u} />)}
      {users.length > max && <span className="tk-av tk-av-more">+{users.length - max}</span>}
    </span>
  );
}

// Image grid used on the ticket cards and the detail page
export function TicketImages({ attachments = [], compact = false }) {
  const images = attachments.filter(a => a?.url && (!a.fileType || a.fileType.startsWith('image/')));
  if (!images.length) return null;
  const shown = compact ? images.slice(0, 4) : images;
  return (
    <div className={`tk-images ${compact ? 'compact' : ''}`}>
      {shown.map((a, i) => (
        <a key={a._id || a.url} href={a.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>
          <img src={a.url} alt={a.filename || 'Attachment'} loading="lazy" />
          {compact && i === shown.length - 1 && images.length > shown.length && (
            <span className="tk-images-more">+{images.length - shown.length}</span>
          )}
        </a>
      ))}
    </div>
  );
}

/* ───────────────────────── Projects hub ───────────────────────── */

function StatCard({ label, icon, value, unit, children }) {
  return (
    <div className="tk-stat">
      <div className="tk-stat-head">
        <span>{label}</span>
        <span className="tk-stat-icon">{icon}</span>
      </div>
      <div className="tk-stat-value"><strong>{value}</strong>{unit && <span>{unit}</span>}</div>
      <div className="tk-stat-foot">{children}</div>
    </div>
  );
}

function ProjectCard({ row, favorite, onToggleFavorite, onOpen }) {
  const { project, total, active, done, last } = row;
  const archived = isArchived(project);
  const progress = total ? Math.round((done / total) * 100) : 0;
  const open = () => onOpen(project);

  return (
    <div
      className={`tk-pcard ${archived ? 'archived' : ''}`}
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
    >
      <div className="tk-pcard-top">
        <span className="tk-key">{projectKey(project.name)}</span>
        <button
          type="button"
          className={`tk-star ${favorite ? 'on' : ''}`}
          aria-label={favorite ? 'Unstar project' : 'Star project'}
          aria-pressed={favorite}
          onClick={e => { e.stopPropagation(); onToggleFavorite(idOf(project)); }}
        >
          <FiStar />
        </button>
        <span className={`tk-pill ${archived ? 'muted' : 'live'}`}>
          <i />{archived ? 'Read-only' : 'Active'}
        </span>
      </div>

      <h3 className="tk-pcard-name">{project.name} {archived && <FiLock />}</h3>
      <p className="tk-pcard-desc">{project.description || project.clientName || 'No description yet.'}</p>

      {archived ? (
        <div className="tk-pcard-archived"><FiInbox /> {cap(project.status)}{project.endDate ? ` on ${format(new Date(project.endDate), 'MMM d, yyyy')}` : ''}</div>
      ) : (
        <div className="tk-progress">
          <div className="tk-progress-label">
            <span><FiFlag /> Ticket progress</span>
            <strong>{progress}%</strong>
          </div>
          <div className="tk-bar"><span style={{ width: `${progress}%` }} /></div>
        </div>
      )}

      <div className="tk-pcard-foot">
        <div className="tk-pcard-foot-row">
          <span className="tk-pcard-count"><FiLayers /> {active} Active {active === 1 ? 'Ticket' : 'Tickets'}</span>
          <span className="tk-muted-sm">{last ? `Updated ${shortDate(last)}` : `${total} total`}</span>
        </div>
        <div className="tk-pcard-foot-row">
          <AvatarStack users={project.members || []} />
          <span className="tk-link">Open Board <FiArrowRight /></span>
        </div>
      </div>
    </div>
  );
}

const PROJECT_FILTERS = [{ key: 'all', label: 'All Projects' }, { key: 'active', label: 'Active' }, { key: 'archived', label: 'Archived' }];
const PROJECT_SORTS = { activity: 'Recent Activity', active: 'Most Active', name: 'Name' };

function ProjectsHub({ onOpen }) {
  const { tickets, fetchTickets, loading } = useShallowStore(useTicketStore, listSelector);
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('activity');
  const [favorites, setFavorites] = useState(loadFavorites);

  useEffect(() => {
    fetchTickets();
    api.get('/projects').then(r => setProjects(r.data.projects || [])).catch(() => setProjects([]));
  }, [fetchTickets]);

  const toggleFavorite = (id) => setFavorites(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    saveFavorites(next);
    return next;
  });

  const allRows = useMemo(() => {
    const byId = new Map();
    const empty = (project) => ({ project, total: 0, active: 0, done: 0, last: null });
    (projects || []).forEach(p => byId.set(idOf(p), empty(p)));
    tickets.forEach(t => {
      const id = idOf(t.project);
      if (!id) return;
      // Tickets can reference a project missing from /projects (e.g. assigned directly)
      if (!byId.has(id)) byId.set(id, empty(t.project));
      const row = byId.get(id);
      row.total += 1;
      if (isActiveTicket(t)) row.active += 1;
      if (isDoneTicket(t)) row.done += 1;
      if (!row.last || new Date(t.updatedAt) > new Date(row.last)) row.last = t.updatedAt;
    });
    return [...byId.values()];
  }, [projects, tickets]);

  const filterCounts = useMemo(() => ({
    all: allRows.length,
    active: allRows.filter(r => !isArchived(r.project)).length,
    archived: allRows.filter(r => isArchived(r.project)).length,
  }), [allRows]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorters = {
      activity: (a, b) => new Date(b.last || b.project.updatedAt || 0) - new Date(a.last || a.project.updatedAt || 0),
      active: (a, b) => b.active - a.active,
      name: (a, b) => (a.project.name || '').localeCompare(b.project.name || ''),
    };
    return allRows
      .filter(r => filter === 'all' || (filter === 'archived') === isArchived(r.project))
      .filter(r => !q || [r.project.name, projectKey(r.project.name), r.project.clientName, r.project.description]
        .some(v => v?.toLowerCase().includes(q)))
      .sort((a, b) =>
        Number(isArchived(a.project)) - Number(isArchived(b.project)) ||
        Number(favorites.has(idOf(b.project))) - Number(favorites.has(idOf(a.project))) ||
        sorters[sort](a, b));
  }, [allRows, filter, search, sort, favorites]);

  const stats = useMemo(() => {
    const weekAgo = Date.now() - 7 * 864e5;
    const active = tickets.filter(isActiveTicket);
    const urgent = active.filter(t => ['high', 'critical'].includes(t.priority)).length;
    const done = tickets.filter(isDoneTicket).length;
    return {
      active: active.length,
      urgent,
      standard: active.length - urgent,
      resolution: tickets.length ? Math.round((done / tickets.length) * 100) : 0,
      newThisWeek: tickets.filter(t => new Date(t.createdAt) > weekAgo).length,
      doneThisWeek: tickets.filter(t => isDoneTicket(t) && new Date(t.updatedAt) > weekAgo).length,
    };
  }, [tickets]);

  const busy = projects === null || (loading && tickets.length === 0);
  const canCreateProject = PROJECT_CREATOR_ROLES.includes(user?.role);

  return (
    <div className="tk-page">
      <div className="tk-crumbs"><span>Bigwals Workspace</span><FiChevronRight /><strong>Tickets</strong></div>

      <div className="tk-hero">
        <div>
          <h1>Projects Hub <span className="tk-version">{filterCounts.active} Active</span></h1>
          <p>Pick a project to see its tickets, or raise a new one in a few words.</p>
        </div>
        {canCreateProject && (
          <button className="tk-btn-primary" onClick={() => navigate('/dashboard/projects')}>
            <FiPlus /> <span>New Project</span>
          </button>
        )}
      </div>

      <div className="tk-toolbar">
        <label className="tk-search">
          <FiSearch />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape') setSearch(''); }}
            placeholder="Search projects by name, key or client..."
          />
          {search && <kbd>ESC to clear</kbd>}
        </label>
        <div className="tk-segment" role="tablist">
          {PROJECT_FILTERS.map(f => (
            <button key={f.key} role="tab" aria-selected={filter === f.key}
              className={filter === f.key ? 'active' : ''} onClick={() => setFilter(f.key)}>
              {f.label} <span>{filterCounts[f.key]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="tk-mobile-summary">
        <span className="tk-stat-icon"><FiAlertCircle /></span>
        <div>
          <strong>{stats.active} Active Issues</strong>
          <small>Across {filterCounts.active} active projects</small>
        </div>
        <span className="tk-pill live"><FiTrendingUp /> {stats.resolution}% resolved</span>
      </div>

      <div className="tk-stats">
        <StatCard label="Total Projects" icon={<FiFolder />} value={filterCounts.all} unit={`${filterCounts.active} active`}>
          <span className="tk-accent"><FiTrendingUp /> {filterCounts.archived} archived</span>
        </StatCard>
        <StatCard label="Active Issues" icon={<FiAlertCircle />} value={stats.active} unit="tickets in flight">
          <span><i className="tk-dot" /> {stats.urgent} high/critical · {stats.standard} standard</span>
        </StatCard>
        <StatCard label="Resolution Rate" icon={<FiCheckCircle />} value={`${stats.resolution}%`} unit={`of ${tickets.length}`}>
          <div className="tk-bar"><span style={{ width: `${stats.resolution}%` }} /></div>
        </StatCard>
        <StatCard label="This Week" icon={<FiZap />} value={stats.newThisWeek} unit="new tickets">
          <span className="tk-accent"><FiCheckCircle /> {stats.doneThisWeek} resolved</span>
        </StatCard>
      </div>

      <div className="tk-section-head">
        <h2>{filter === 'archived' ? 'Archived Projects' : filter === 'active' ? 'Active Projects' : 'Active & Archived Projects'}
          <span className="tk-chip-sm">Showing {rows.length}</span>
        </h2>
        <label className="tk-orderby">
          <span className="tk-orderby-label">Order by:</span>
          <select value={sort} onChange={e => setSort(e.target.value)}>
            {Object.entries(PROJECT_SORTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
      </div>

      {busy ? (
        <div className="tk-pgrid" aria-hidden="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="tk-pcard">
              <SkeletonBlock width={70} height={20} radius={6} style={{ marginBottom: 14 }} />
              <SkeletonLine width="55%" height={18} style={{ marginBottom: 10 }} />
              <SkeletonLine width="90%" height={12} style={{ marginBottom: 18 }} />
              <SkeletonLine width="100%" height={6} />
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="tk-empty">
          <FiInbox size={42} />
          <p>{search ? 'No projects match your search.' : 'No projects here yet.'}</p>
        </div>
      ) : (
        <div className="tk-pgrid">
          {rows.map(row => (
            <ProjectCard
              key={idOf(row.project)}
              row={row}
              favorite={favorites.has(idOf(row.project))}
              onToggleFavorite={toggleFavorite}
              onOpen={onOpen}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Project tickets ───────────────────────── */

// Message-style composer: whatever is typed (plus images) becomes a new ticket.
// Desktop shows it as a side panel, phones as a single chat bar pinned to the bottom.
function TicketComposer({ project, inputRef }) {
  const { sendTicket } = useShallowStore(useTicketStore, sendSelector);
  const { user } = useAuthStore();
  const [text, setText] = useState('');
  const [priority, setPriority] = useState('medium');
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const previews = useMemo(() => files.map(f => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach(URL.revokeObjectURL), [previews]);

  const addFiles = (list) => {
    const all = [...list];
    const images = all.filter(f => f.type.startsWith('image/'));
    if (images.length < all.length) toast.error('Only images can be attached');
    setFiles(prev => {
      const next = [...prev, ...images];
      if (next.length > MAX_IMAGES) toast.error(`Up to ${MAX_IMAGES} images per ticket`);
      return next.slice(0, MAX_IMAGES);
    });
  };

  // Clears straight away; the store shows the ticket as "Sending…" until it's saved
  const submit = (e) => {
    e?.preventDefault();
    const description = text.trim();
    if (!description && !files.length) return;
    sendTicket({ project, description, priority, files, user }).then(ticket => {
      if (!ticket) setText(current => current || description);
    });
    setText('');
    setFiles([]);
    setPriority('medium');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit(e);
  };

  const handlePaste = (e) => {
    const pasted = [...(e.clipboardData?.files || [])];
    if (pasted.length) { e.preventDefault(); addFiles(pasted); }
  };

  const dropProps = {
    onDragOver: (e) => { e.preventDefault(); setDragging(true); },
    onDragLeave: () => setDragging(false),
    onDrop: (e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); },
  };

  if (isArchived(project)) {
    return (
      <aside className="tk-composer-panel readonly">
        <FiLock /> This project is {project.status} — tickets are read-only.
      </aside>
    );
  }

  const canSend = Boolean(text.trim() || files.length);
  const openPicker = () => fileRef.current?.click();

  return (
    <form className={`tk-composer-panel ${dragging ? 'dragging' : ''}`} onSubmit={submit} {...dropProps}>
      <div className="tk-cp-head">
        <span className="tk-cp-icon"><FiZap /></span>
        <div>
          <h3>Create New Ticket</h3>
          <small>Project: {project.name}</small>
        </div>
      </div>

      <label className="tk-cp-label" htmlFor="tk-new-text">Describe the issue</label>
      <div className="tk-cp-inputrow">
        <span className="tk-cp-lead tk-only-mobile" aria-hidden="true"><FiEdit3 /></span>
        <textarea
          id="tk-new-text"
          ref={inputRef}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder="Describe the issue or task..."
          rows={5}
        />
      </div>

      {files.length > 0 && (
        <div className="tk-cp-previews">
          {previews.map((src, i) => (
            <div key={src} className="tk-cp-preview">
              <img src={src} alt="" />
              <button type="button" aria-label="Remove image"
                onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}>
                <FiX />
              </button>
            </div>
          ))}
        </div>
      )}

      <label className="tk-cp-label" htmlFor="tk-new-priority">Priority</label>
      <div className="tk-cp-tools">
        <span className={`tk-priority-select prio-${priority}`}>
          <i /><FiFlag className="tk-only-mobile" />
          <select id="tk-new-priority" value={priority} onChange={e => setPriority(e.target.value)}>
            {PRIORITIES.map(p => <option key={p} value={p}>{cap(p)}</option>)}
          </select>
        </span>
        <button type="button" className="tk-cp-attach" onClick={openPicker}
          disabled={files.length >= MAX_IMAGES} aria-label="Attach images">
          <FiImage />
        </button>
        <button type="submit" className="tk-btn-primary tk-cp-create tk-only-mobile" disabled={!canSend}>
          <FiZap /> Create Ticket
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple hidden
        onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />

      <button type="button" className="tk-dropzone" onClick={openPicker}>
        <FiUploadCloud /> Drag screenshots here, paste, or click to attach
      </button>

      <div className="tk-cp-submit">
        <span className="tk-muted-sm"><kbd>⌘ + Enter</kbd> to publish</span>
        <button type="submit" className="tk-btn-primary" disabled={!canSend}>
          <FiZap /> Create Ticket
        </button>
      </div>

      <p className="tk-cp-note">The first line becomes the ticket title. No need to assign anyone.</p>
    </form>
  );
}

// Body text without the first line when that line is the title
const bodyOf = (t) => {
  const desc = (t.description || '').trim();
  const [first, ...rest] = desc.split('\n');
  return first.trim() === t.title ? rest.join('\n').trim() : desc;
};

function TicketCard({ ticket: t, onSelect }) {
  const stage = stageOf(t.status);
  const imageCount = (t.attachments || []).length;
  const body = bodyOf(t);
  const select = () => { if (!t.pending) onSelect(t._id); };

  return (
    <article
      className={`tk-tcard edge-${t.pending ? 'pending' : stage} ${t.pending ? 'pending' : ''}`}
      role="button"
      tabIndex={t.pending ? -1 : 0}
      aria-disabled={t.pending || undefined}
      onClick={select}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(); } }}
    >
      <div className="tk-tcard-top">
        {t.ticketNumber && <span className="tk-key">{t.ticketNumber}</span>}
        {imageCount > 0 && <span className="tk-label"><FiImage /> {imageCount}</span>}
        {t.pending
          ? <span className="tk-status s-sending"><span className="tk-spinner" /> Sending…</span>
          : <span className={`tk-status s-${stage}`}>{STATUS_ICONS[stage]} {STATUS_LABELS[t.status]}</span>}
      </div>
      <h3 className="tk-tcard-title">{t.title}</h3>
      {body && <p className="tk-tcard-body">{body}</p>}
      <TicketImages attachments={t.attachments} compact />
      <div className="tk-tcard-foot">
        <span className="tk-person"><Avatar user={t.createdBy} size={22} /> {personName(t.createdBy) || '—'}</span>
        <span className="tk-muted-sm tk-time"><FiClock /> {shortTime(t.createdAt)}</span>
        <span className="tk-foot-right">
          <span className={`tk-prio prio-${t.priority}`}><FiFlag /> {cap(t.priority)}</span>
          {t.commentCount > 0 && <span className="tk-comments"><FiMessageSquare /> {t.commentCount}</span>}
        </span>
      </div>
    </article>
  );
}

const STAGE_FILTERS = [{ key: 'all', label: 'All' }, ...STAGES, { key: 'closed', label: 'Closed' }];
const TICKET_SORTS = {
  updated: (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
  newest: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  oldest: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
  priority: (a, b) => PRIORITIES.indexOf(b.priority) - PRIORITIES.indexOf(a.priority),
};
const SORT_LABELS = { updated: 'last_updated', newest: 'newest', oldest: 'oldest', priority: 'priority' };

function ProjectTicketsView({ project, onBack, onSelectTicket }) {
  const { tickets, fetchTickets, loading } = useShallowStore(useTicketStore, listSelector);
  const [stageFilter, setStageFilter] = useState('all');
  const [raisedBy, setRaisedBy] = useState('');
  const [priority, setPriority] = useState('');
  const [sort, setSort] = useState('updated');
  const inputRef = useRef(null);
  const projectId = idOf(project);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const projectTickets = useMemo(() => tickets.filter(t => idOf(t.project) === projectId), [tickets, projectId]);

  const people = useMemo(() => {
    const byId = new Map();
    projectTickets.forEach(t => t.createdBy && byId.set(idOf(t.createdBy), t.createdBy));
    return [...byId.values()].sort((a, b) => personName(a).localeCompare(personName(b)));
  }, [projectTickets]);

  // Everything except the stage filter, so the chip counts reflect the other filters
  const baseFiltered = useMemo(() => projectTickets.filter(t =>
    (!raisedBy || idOf(t.createdBy) === raisedBy) && (!priority || t.priority === priority)
  ), [projectTickets, raisedBy, priority]);

  const counts = useMemo(() => {
    const c = { all: baseFiltered.length };
    baseFiltered.forEach(t => { const s = stageOf(t.status); c[s] = (c[s] || 0) + 1; });
    return c;
  }, [baseFiltered]);

  // Tickets still being sent stay on top whatever the filter
  const visible = useMemo(() => {
    const sending = projectTickets.filter(t => t.pending);
    const rest = (stageFilter === 'all' ? baseFiltered : baseFiltered.filter(t => stageOf(t.status) === stageFilter))
      .filter(t => !t.pending)
      .sort(TICKET_SORTS[sort]);
    return [...sending, ...rest];
  }, [projectTickets, baseFiltered, stageFilter, sort]);

  const activeCount = projectTickets.filter(isActiveTicket).length;
  const filtersOn = raisedBy || priority || stageFilter !== 'all';
  const resetFilters = () => { setRaisedBy(''); setPriority(''); setStageFilter('all'); };
  const nextSort = () => {
    const keys = Object.keys(TICKET_SORTS);
    setSort(keys[(keys.indexOf(sort) + 1) % keys.length]);
  };
  const lastSync = projectTickets.filter(t => !t.pending).map(t => new Date(t.updatedAt));

  return (
    <div className="tk-page tk-board">
      <div className="tk-crumbs">
        <button type="button" onClick={onBack}>Projects</button><FiChevronRight />
        <span>{project.name}</span><FiChevronRight /><strong>Tickets</strong>
      </div>

      <div className="tk-mcrumb">
        <button type="button" onClick={onBack}><FiChevronLeft /> Projects</button>
        <span>/</span>
        <strong>{project.name}</strong>
      </div>

      <div className="tk-hero">
        <h1><span className="tk-title-main">{project.name}</span> <span className="tk-title-sub"><span className="tk-title-dash">— </span>Issues &amp; Backlog</span>
          <span className="tk-version">{activeCount} Active {activeCount === 1 ? 'Ticket' : 'Tickets'}</span>
        </h1>
        {!isArchived(project) && (
          <button className="tk-btn-primary tk-hide-mobile" onClick={() => inputRef.current?.focus()}>
            <FiPlus /> New Ticket
          </button>
        )}
      </div>

      <div className="tk-board-grid">
        <div className="tk-board-main">
          <div className="tk-filterbar">
            <div className="tk-chips" role="tablist">
              {STAGE_FILTERS.map(s => (
                <button key={s.key} role="tab" aria-selected={stageFilter === s.key}
                  className={stageFilter === s.key ? 'active' : ''} onClick={() => setStageFilter(s.key)}>
                  {s.label} <span>{counts[s.key] || 0}</span>
                </button>
              ))}
            </div>
            <div className="tk-dropdowns">
              <select value={raisedBy} onChange={e => setRaisedBy(e.target.value)} aria-label="Raised by">
                <option value="">Raised by: Anyone</option>
                {people.map(p => <option key={idOf(p)} value={idOf(p)}>{personName(p)}</option>)}
              </select>
              <select value={priority} onChange={e => setPriority(e.target.value)} aria-label="Priority">
                <option value="">Priority: All</option>
                {PRIORITIES.map(p => <option key={p} value={p}>{cap(p)}</option>)}
              </select>
              <button type="button" className="tk-icon-btn-sm" onClick={resetFilters} disabled={!filtersOn} aria-label="Reset filters" title="Reset filters">
                <FiRotateCcw />
              </button>
            </div>
          </div>

          <div className="tk-list-head">
            <span>Backlog<span className="tk-hide-mobile"> · synced {lastSync.length ? formatDistanceToNowStrict(new Date(Math.max(...lastSync))) + ' ago' : 'just now'}</span></span>
            <button type="button" onClick={nextSort}>sort: {SORT_LABELS[sort]} ⇅</button>
          </div>

          <div className="tk-tlist">
            {loading && projectTickets.length === 0 ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="tk-tcard" aria-hidden="true">
                  <SkeletonBlock width={60} height={18} radius={5} style={{ marginBottom: 12 }} />
                  <SkeletonLine width="70%" height={16} style={{ marginBottom: 8 }} />
                  <SkeletonLine width="90%" height={12} />
                </div>
              ))
            ) : visible.length === 0 ? (
              <div className="tk-empty">
                <FiInbox size={40} />
                <p>{projectTickets.length === 0 ? 'No tickets yet — describe an issue to raise the first one.' : 'No tickets match these filters.'}</p>
              </div>
            ) : (
              visible.map(t => <TicketCard key={t._id} ticket={t} onSelect={onSelectTicket} />)
            )}
            {visible.length > 0 && (
              <div className="tk-list-foot"><FiCheckCircle /> Showing {visible.length} of {projectTickets.length} tickets</div>
            )}
          </div>
        </div>

        <TicketComposer project={project} inputRef={inputRef} />
      </div>
    </div>
  );
}

/**
 * Project-first tickets screen. `renderDetail(ticketId, onBack, projectName)` draws the
 * role-specific ticket detail page.
 */
export default function ProjectTickets({ renderDetail }) {
  const [project, setProject] = useState(null);
  const [ticketId, setTicketId] = useState(null);

  if (project && ticketId) return renderDetail(ticketId, () => setTicketId(null), project.name);
  if (project) return <ProjectTicketsView project={project} onBack={() => setProject(null)} onSelectTicket={setTicketId} />;
  return <ProjectsHub onOpen={setProject} />;
}
